/**
 * Answer Campaign（回答実施）Management Page
 * 会社ごとに GB人財 の回答実施（回答開始日）を登録・編集・削除する。ADMIN 専用。
 * 「現在有効」= 開始日 <= 本日 の中で最も新しい回。社員はこの回に1回だけ回答でき、
 * 組織/個人レポートはこの回のレコードで作成される。設計: docs/回答実施_設計_2026-07-29.md
 */
import { useState, useEffect } from 'react';
import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, Alert, CircularProgress,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { organizationApi } from '../api/organizationApi';
import { answerCampaignApi, type AnswerCampaign } from '../api/answerCampaignApi';
import { questionSetApi } from '../api/questionSetApi';
import type { Company } from '../types/organization.types';
import type { QuestionSetListItem } from '../types/questionSet.types';

const today = () => new Date().toISOString().slice(0, 10);

/** 一覧（開始日降順）の中で「現在有効な回」の id を返す（開始日<=本日 の先頭） */
const activeCampaignId = (campaigns: AnswerCampaign[]): string | null => {
  const t = today();
  const active = campaigns.find((c) => c.startDate <= t); // 降順なので先頭が最新
  return active?.id ?? null;
};

export const AnswerCampaignManagementPage = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [questionSets, setQuestionSets] = useState<QuestionSetListItem[]>([]);
  const [campaigns, setCampaigns] = useState<AnswerCampaign[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState<AnswerCampaign | null>(null);
  const [formStartDate, setFormStartDate] = useState(today());
  const [formQuestionSetId, setFormQuestionSetId] = useState('');
  const [formLabel, setFormLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [toDelete, setToDelete] = useState<AnswerCampaign | null>(null);

  useEffect(() => {
    loadInitial();
  }, []);

  useEffect(() => {
    if (selectedCompanyId) loadCampaigns(selectedCompanyId);
  }, [selectedCompanyId]);

  const loadInitial = async () => {
    try {
      setLoading(true);
      const [companyData, qsData] = await Promise.all([
        organizationApi.getCompanies(),
        questionSetApi.getAll().catch(() => [] as QuestionSetListItem[]),
      ]);
      setCompanies(companyData);
      setQuestionSets(qsData);
      if (companyData.length > 0) setSelectedCompanyId(companyData[0].id);
    } catch {
      setError('初期データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const loadCampaigns = async (companyId: string) => {
    try {
      setLoading(true);
      const data = await answerCampaignApi.list(companyId);
      setCampaigns(data);
    } catch {
      setError('回答実施一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (campaign?: AnswerCampaign) => {
    setError(null);
    if (campaign) {
      setEditing(campaign);
      setFormStartDate(campaign.startDate);
      setFormQuestionSetId(campaign.questionSetId ?? '');
      setFormLabel(campaign.label ?? '');
    } else {
      setEditing(null);
      setFormStartDate(today());
      setFormQuestionSetId('');
      setFormLabel('');
    }
    setOpenDialog(true);
  };

  const handleSave = async () => {
    if (!formStartDate) return;
    try {
      setSaving(true);
      if (editing) {
        await answerCampaignApi.update(editing.id, {
          startDate: formStartDate,
          questionSetId: formQuestionSetId || null,
          label: formLabel.trim() || null,
        });
        setSuccess('回答実施を更新しました');
      } else {
        await answerCampaignApi.create({
          companyId: selectedCompanyId,
          startDate: formStartDate,
          questionSetId: formQuestionSetId || null,
          label: formLabel.trim() || null,
        });
        setSuccess('回答実施を登録しました');
      }
      setOpenDialog(false);
      await loadCampaigns(selectedCompanyId);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setError(status === 409 ? 'その会社・開始日の回答実施は既に存在します' : '回答実施の保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await answerCampaignApi.remove(toDelete.id);
      setSuccess('回答実施を削除しました');
      setDeleteDialogOpen(false);
      await loadCampaigns(selectedCompanyId);
    } catch {
      setError('回答実施の削除に失敗しました');
    }
  };

  const activeId = activeCampaignId(campaigns);

  return (
    <Box>
      <Typography variant="h4" mb={1}>回答実施（GB人財）</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        会社ごとに回答開始日を設定します。「現在有効」の回に社員は1回だけ回答でき、組織・個人レポートはその回のデータで作成されます。
      </Typography>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}

      <Box display="flex" gap={2} mb={3}>
        <FormControl sx={{ minWidth: 300 }}>
          <InputLabel>会社を選択</InputLabel>
          <Select value={selectedCompanyId} onChange={(e) => setSelectedCompanyId(e.target.value)} label="会社を選択">
            {companies.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </Select>
        </FormControl>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()} disabled={!selectedCompanyId}>
          回答実施を追加
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
      ) : (
        <Card>
          <CardContent>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>回答開始日</TableCell>
                    <TableCell>状態</TableCell>
                    <TableCell>ラベル</TableCell>
                    <TableCell>質問セット</TableCell>
                    <TableCell>作成日</TableCell>
                    <TableCell align="right">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {campaigns.length === 0 ? (
                    <TableRow><TableCell colSpan={6} align="center">回答実施が登録されていません</TableCell></TableRow>
                  ) : (
                    campaigns.map((c) => {
                      const isActive = c.id === activeId;
                      const isFuture = c.startDate > today();
                      return (
                        <TableRow key={c.id}>
                          <TableCell>{c.startDate}</TableCell>
                          <TableCell>
                            {isActive && <Chip label="現在有効" color="success" size="small" />}
                            {isFuture && <Chip label="開始前" color="default" size="small" variant="outlined" />}
                            {!isActive && !isFuture && <Chip label="過去" color="default" size="small" variant="outlined" />}
                          </TableCell>
                          <TableCell>{c.label || '—'}</TableCell>
                          <TableCell>{c.questionSetTitle || '（未指定）'}</TableCell>
                          <TableCell>{c.createdAt ? new Date(c.createdAt).toLocaleDateString() : '—'}</TableCell>
                          <TableCell align="right">
                            <IconButton onClick={() => handleOpenDialog(c)} color="primary" size="small"><EditIcon /></IconButton>
                            <IconButton onClick={() => { setToDelete(c); setDeleteDialogOpen(true); }} color="error" size="small"><DeleteIcon /></IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? '回答実施の編集' : '回答実施の追加'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus margin="dense" label="回答開始日" type="date" fullWidth
            value={formStartDate} onChange={(e) => setFormStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            margin="dense" label="ラベル（任意・例: 2026年上期）" fullWidth
            value={formLabel} onChange={(e) => setFormLabel(e.target.value)}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>質問セット（任意）</InputLabel>
            <Select
              value={formQuestionSetId} label="質問セット（任意）"
              onChange={(e) => setFormQuestionSetId(e.target.value)}
            >
              <MenuItem value=""><em>（未指定）</em></MenuItem>
              {questionSets.map((qs) => <MenuItem key={qs.id} value={qs.id}>{qs.title}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>キャンセル</Button>
          <Button onClick={handleSave} variant="contained" disabled={!formStartDate || saving}>保存</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>回答実施の削除</DialogTitle>
        <DialogContent>
          <Typography>
            回答開始日「{toDelete?.startDate}」の回答実施を削除してもよろしいですか?<br />
            <Typography component="span" variant="body2" color="text.secondary">
              ※ 既存の回答・レポートの紐付けは解除されます（回答データ自体は残ります）。
            </Typography>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleDelete} color="error" variant="contained">削除</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
